import {isArray} from '@naverpay/hidash/isArray'
import {isObject} from '@naverpay/hidash/isObject'
import {isPlainObject} from '@naverpay/hidash/isPlainObject'
import {isEmpty, every, mergeWith} from 'lodash/fp'

type RecursiveRequired<T> = {
    [P in keyof T]: RecursiveRequired<NonNullable<T[P]>>
}

/** object의 필요 없는 값을 제거 */
export const removeFalsyValues = <T extends object>(obj: T) => {
    const result = {} as RecursiveRequired<T>
    for (const key in obj) {
        const value = obj[key]
        if (isObject(value) && !isArray(value)) {
            // 객체일 때 deep traverse
            const innerResult = removeFalsyValues(value)
            if (!isEmpty(innerResult)) {
                Object.assign(result, {[key]: innerResult})
            }
        } else if (typeof value === 'number' || typeof value === 'boolean') {
            // 숫자나 bool 같은 경우는 0, false도 있는 값이기 때문에 할당
            Object.assign(result, {[key]: value})
        } else if (typeof value === 'string') {
            // 같은 if에 있으면 '  ' 같은 값이 else 블락으로 넘어가게된다
            if (value.trim() !== '') {
                // string은 white space 제거 후 빈 스트링이 아니면 할당
                Object.assign(result, {[key]: value.trim()})
            }
        } else if (value) {
            // 그 외 undefined, NaN값들 걸러낸 후 할당
            Object.assign(result, {[key]: value})
        }
    }
    return result
}

/** object의 null, undefined 를 empty string 으로 초기화 */
export const replaceFalsyToEmptyString = <T extends object>(obj: T) => {
    const result = {}
    for (const key in obj) {
        const value = obj[key]
        if (isObject(value) && !isArray(value)) {
            // 객체인 경우 deep copy 하면서 falsy는 empty string으로 대체
            const innerResult = replaceFalsyToEmptyString(value)
            if (!isEmpty(innerResult)) {
                Object.assign(result, {[key]: innerResult})
            }
        } else if (typeof value === 'number' || typeof value === 'boolean') {
            Object.assign(result, {[key]: value})
        } else if (typeof value === 'string') {
            // string은 empty string 포함 그대로 할당
            Object.assign(result, {[key]: value.trim()})
        } else if (!value) {
            // falsy 값은 empty string으로 할당
            Object.assign(result, {[key]: ''})
        }
    }
    return result as T
}

/** 병합할 속성 값이 null | undefined 가 아닌 경우에만 merge 합니다.(shallow merge) */
export const mergeWithValid = mergeWith((nextValue: unknown, orgValue: unknown) => nextValue ?? orgValue)

/** 병합할 속성 값이 null | undefined 가 아닌 경우에만 merge 합니다.(deep merge) */
export const deepMergeWithValid = mergeWith((nextValue: unknown, orgValue: unknown) => {
    return every(isPlainObject)([nextValue, orgValue]) ? undefined : (nextValue ?? orgValue)
})

/** 병합할 속성 값이 null | undefined 여도 merge 합니다.(deep merge) */
export const deepMerge = mergeWith((nextValue: unknown, orgValue: unknown) => {
    return every(isPlainObject)([nextValue, orgValue]) ? undefined : nextValue
})

/** 객체에 props에 명시된 모든 프로퍼티가 있는지 확인 */
export const hasEveryOwnProperty = <A extends object>(obj: object, props: (keyof A)[]): obj is A =>
    props.every((prop) => Object.prototype.hasOwnProperty.call(obj, prop))
